import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../../config';

const Documentation = () => {
  const [error, setError] = useState(null);
  const [docList, setDocList] = useState([]);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [loadingDoc, setLoadingDoc] = useState(false);
  const [loadingList, setLoadingList] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editContent, setEditContent] = useState('');
  const [savingDoc, setSavingDoc] = useState(false);
  const [addNewMode, setAddNewMode] = useState(false);

  const getAdminEmail = () => {
    try {
      const chatbotUser = localStorage.getItem('chatbotUser');
      if (chatbotUser) {
        const user = JSON.parse(chatbotUser);
        if (user && user.email) return user.email.trim();
      }
      return (localStorage.getItem('userEmail') || '').toString().trim();
    } catch (e) {
      return (localStorage.getItem('userEmail') || '').toString().trim();
    }
  };

  const isAdmin = () => getAdminEmail().toLowerCase() === 'info@urcommunity.com.au';

  const fetchDocList = async () => {
    setLoadingList(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/documentation`);
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success && Array.isArray(data.data)) setDocList(data.data);
    } catch (e) {
      setDocList([]);
    } finally {
      setLoadingList(false);
    }
  };

  const fetchDocById = async (id) => {
    setSelectedDoc(null);
    setLoadingDoc(true);
    setEditTitle('');
    setEditContent('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/documentation/${id}`);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to load document');
      if (data.success && data.data) {
        setSelectedDoc(data.data);
        setEditTitle(data.data.title || '');
        setEditContent(data.data.content || '');
      }
    } catch (err) {
      setError(err.message || 'Failed to load document');
    } finally {
      setLoadingDoc(false);
    }
  };

  const saveDocToTable = async () => {
    if (!selectedDoc && !addNewMode) return;
    if (addNewMode && !editTitle.trim() && !editContent.trim()) return;
    setSavingDoc(true);
    setError(null);
    try {
      const userEmail = getAdminEmail();
      if (!userEmail) {
        setError('Admin email not found.');
        return;
      }
      const res = await fetch(`${API_BASE_URL}/api/admin/documentation-doc`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'x-admin-email': userEmail },
        body: JSON.stringify({
          email: userEmail,
          id: selectedDoc ? selectedDoc.id : undefined,
          title: editTitle.trim(),
          content: editContent
        })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to save');
      if (data.success && data.data) {
        const wasNewDoc = addNewMode;
        setAddNewMode(false);
        fetchDocList();
        if (wasNewDoc) {
          // New document: close form so next Save creates another row. Admin must click "Add Document" again.
          setSelectedDoc(null);
          setEditTitle('');
          setEditContent('');
        } else {
          // Editing existing: keep it selected so they can edit further.
          setSelectedDoc(data.data);
          setEditTitle(data.data.title || '');
          setEditContent(data.data.content || '');
        }
      }
    } catch (err) {
      setError(err.message || 'Failed to save document');
    } finally {
      setSavingDoc(false);
    }
  };

  useEffect(() => {
    if (!isAdmin()) {
      setError('Access denied. Admin privileges required.');
      return;
    }
    fetchDocList();
  }, []);

  if (!isAdmin()) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <h2 style={{ color: '#dc2626' }}>Access Denied</h2>
        <p>Admin privileges required to access this page.</p>
      </div>
    );
  }

  return (
    <div style={{ background: '#f9fafb', minHeight: '100vh', padding: '24px' }}>
      <div style={{ maxWidth: '900px', margin: '0 auto' }}>
        <div style={{ background: 'white', borderRadius: '12px', padding: '24px', marginBottom: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <h1 style={{ fontSize: '1.5rem', fontWeight: '700', color: '#111827', margin: '0 0 8px 0' }}>Documentation</h1>
          <p style={{ fontSize: '0.875rem', color: '#6b7280', margin: 0 }}>Click &quot;Add Document&quot; to store details in the documentation table. Documents are listed below and shown in the footer when users click Documentation.</p>
        </div>

        {error && (
          <div style={{ background: '#fee2e2', border: '1px solid #fecaca', borderRadius: '8px', padding: '12px', color: '#dc2626', marginBottom: '16px' }}>
            {error}
            <button type="button" onClick={() => setError(null)} style={{ background: 'none', border: 'none', color: '#dc2626', float: 'right', cursor: 'pointer' }}>×</button>
          </div>
        )}

        {/* Single box: Add Document onclick → form → save to documentation table → list */}
        <div style={{ background: 'white', borderRadius: '12px', padding: '24px', marginBottom: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <button
            type="button"
            onClick={() => { setAddNewMode(true); setSelectedDoc(null); setEditTitle(''); setEditContent(''); setLoadingDoc(false); }}
            style={{
              padding: '10px 20px',
              fontSize: '14px',
              fontWeight: '600',
              color: 'white',
              background: '#7c3aed',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              marginBottom: '20px'
            }}
          >
            Add Document
          </button>

          {(selectedDoc || addNewMode) && !loadingDoc && (
            <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '16px', marginBottom: '24px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>Title</label>
              <input
                type="text"
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                placeholder="Title"
                style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box', marginBottom: '12px' }}
              />
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>Content</label>
              <textarea
                value={editContent}
                onChange={(e) => setEditContent(e.target.value)}
                placeholder="Content"
                rows={6}
                style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit', marginBottom: '12px' }}
              />
              <button
                type="button"
                onClick={saveDocToTable}
                disabled={savingDoc}
                style={{
                  padding: '10px 20px',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: 'white',
                  background: savingDoc ? '#9ca3af' : '#7c3aed',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: savingDoc ? 'not-allowed' : 'pointer'
                }}
              >
                {savingDoc ? 'Saving...' : addNewMode ? 'Save' : 'Update'}
              </button>
            </div>
          )}

          <h2 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#374151', margin: '0 0 12px 0' }}>Documents</h2>
          {loadingList ? (
            <p style={{ color: '#6b7280' }}>Loading list...</p>
          ) : (
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {docList.map((d) => (
                <li key={d.id}>
                  <button
                    type="button"
                    onClick={() => fetchDocById(d.id)}
                    style={{
                      width: '100%',
                      textAlign: 'left',
                      padding: '12px 16px',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      background: selectedDoc && selectedDoc.id === d.id ? '#ede9fe' : 'white',
                      cursor: 'pointer',
                      fontSize: '14px',
                      color: '#111827'
                    }}
                  >
                    {d.title || `Document #${d.id}`}
                  </button>
                </li>
              ))}
              {docList.length === 0 && !loadingList && <li style={{ color: '#6b7280' }}>No documents yet. Click &quot;Add Document&quot; to add one.</li>}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default Documentation;
